import React, { useEffect, useState } from 'react';
import { Combobox, Option } from '@fluentui/react-components';
import type { ComboboxProps } from '@fluentui/react-components';
import { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';

interface PageData<T> {
  items: T[];
  totalCount: number;
}

interface OptionType {
  value: string;
  text: string;
}

interface InfiniteDropdownProps<T> {
  queryResult: UseInfiniteQueryResult<InfiniteData<PageData<T>>, unknown>;
  itemToOption: (item: T) => OptionType;
  onChange?: (event: React.SyntheticEvent | null, data: { value: string }) => void;
  onFilterChange?: (filter: string) => void;
  placeholder?: string;
  name?: string;
  required?: boolean;
  value?: string;
  selectedItem?: T;
  clearable?: boolean;
}

const InfiniteDropdown = <T extends {}>({
  queryResult: {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  },
  itemToOption,
  placeholder = 'Select an option',
  onChange,
  onFilterChange,
  name,
  required,
  value,
  selectedItem,
  clearable = false
}: InfiniteDropdownProps<T>) => {
  const [options, setOptions] = useState<OptionType[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (data?.pages) {
      let newOptions = data.pages.flatMap(page => 
        page.items.map(itemToOption)
      );
      
      // Add selectedItem to options if it exists and isn't already in the list
      if (selectedItem) {
        const selectedOption = itemToOption(selectedItem);
        if (!newOptions.some(opt => opt.value === selectedOption.value)) {
          newOptions.unshift(selectedOption);
        }
      }

      // // Add empty option if clearable is true
      // if (clearable) {
      //   newOptions = [{ value: '', text: placeholder }, ...newOptions];
      // }
      
      setOptions(newOptions);
    }
  }, [data, itemToOption, selectedItem, placeholder]);

  useEffect(() => {
    if (value && !options.some(opt => opt.value === value) && selectedItem) {
      const selectedOption = itemToOption(selectedItem);
      setOptions(prev => {
        if (!prev.some(opt => opt.value === selectedOption.value)) {
          return [selectedOption, ...prev];
        }
        return prev;
      });
    }
  }, [value, options, selectedItem, itemToOption]);

  useEffect(() => {
    if (!isOpen) return;

    setTimeout(() => {
      const listbox = document.querySelector('.infinite-select__listbox');
      if (!listbox) return;

      const handleScroll = (e: Event) => {
        const target = e.target as HTMLElement;
        const isScrolledToBottom = 
          Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 1;

        if (isScrolledToBottom && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      };

      listbox.addEventListener('scroll', handleScroll);
      return () => listbox.removeEventListener('scroll', handleScroll);
    }, 100);
  }, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const resetFilter = () => {
    setFilter('');
    onFilterChange?.('');
  };

  const handleInputChange: ComboboxProps['onChange'] = (event) => {
    const newFilter = event.target.value.trim();
    setFilter(newFilter);
    onFilterChange?.(newFilter);
  };

  const commonListboxProps = {
    className: "infinite-select__listbox",
    style: {
      maxHeight: '300px',
      overflowY: 'auto' as const
    }
  } as const;

  const renderOptions = () => (
    <>
      {options.map((option) => (
        <Option key={option.value} value={option.value} text={option.text}>
          {option.text}
        </Option>
      ))}
      {isFetchingNextPage && (
        <Option key="loading" value="" disabled>
          Loading...
        </Option>
      )}
      {hasNextPage && !isFetchingNextPage && (
        <Option key="more" value="" disabled>
          ...
        </Option>
      )}
    </>
  );

  return (
    <Combobox
      name={name}
      onOptionSelect={(e, data) => {
        if (onChange) {
          if (!data.optionValue && data.selectedOptions.length === 0) {
            // Handle clearing
            onChange(e, { value: '' });
            resetFilter();
            return;
          }
          const selectedOption = options.find(opt => opt.value === data.optionValue);
          if (selectedOption) {
            onChange(e, { value: selectedOption.value });
            resetFilter();
          }
        }
      }}
      onOpenChange={(e, data) => setIsOpen(data.open)}
      required={required}
      value={isFocused 
        ? filter || options.find(opt => opt.value === value)?.text || '' 
        : options.find(opt => opt.value === value)?.text || (selectedItem ? itemToOption(selectedItem).text : '')}
      selectedOptions={value ? [value] : []}
      placeholder={placeholder}
      className="infinite-select"
      positioning={{ 
        autoSize: "width",
        position: "below",
        flipBoundary: "window"
      }}
      freeform
      onChange={handleInputChange}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        resetFilter();
      }}
      listbox={commonListboxProps}
      clearable={clearable}
    >
      {renderOptions()}
    </Combobox>
  );
};

export default InfiniteDropdown;